import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Article,
  Dashboard,
  Group,
  VideoLibrary,
  Work,
} from '@mui/icons-material';
import { ChevronLeft, ChevronRight, House, LogOut, User, X } from 'lucide-react';
import { logoutUser } from '../../../../services/session';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';
import { PRODUCT_IMAGES } from '../../../../branding/assets';

const navItems = [
  { text: 'Dashboard', icon: <Dashboard />, to: '/cv', end: true },
  { text: 'Vagas', icon: <Work />, to: '/cv/jobs' },
  { text: 'Etapas', icon: <Article />, to: '/cv/etapas' },
  { text: 'Candidaturas', icon: <Group />, to: '/cv/applications' },
];

const footerNavItems = [
  { text: 'Tutorial', icon: <VideoLibrary />, to: '/cv/tutorial', highlight: true },
];

const Sidebar = ({
  drawerWidth = 240,
  open = true,
  mobileOpen = false,
  isMobile = false,
  onCloseMobile,
  onNavigate,
  onToggle,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const mini = !isMobile && !open;
  const { user } = useAuthSession();
  const userName = user?.name || user?.username || 'Usuário';

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleNavClick = () => {
    onNavigate?.();
  };

  const toggleLabel = isMobile
    ? 'Fechar menu'
    : open
      ? 'Recolher menu'
      : 'Expandir menu';

  const headerButtonSx = {
    position: 'relative',
    zIndex: 1,
    width: isMobile ? 44 : 36,
    height: isMobile ? 44 : 36,
    color: '#ffffff',
    bgcolor: 'rgba(255, 255, 255, 0.10)',
    border: '1px solid rgba(255, 255, 255, 0.28)',
    '&:hover': {
      bgcolor: 'rgba(255, 255, 255, 0.18)',
      borderColor: 'rgba(255, 255, 255, 0.42)',
    },
  };

  const renderNavItem = (item) => (
    <Tooltip title={item.text} placement="right" key={item.text} disableHoverListener={!mini}>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <NavLink
          to={item.to}
          end={item.end}
          onClick={handleNavClick}
          style={{ color: 'inherit', display: 'block', textDecoration: 'none' }}
        >
          {({ isActive }) => (
            <ListItemButton
              selected={isActive}
              sx={{
                minHeight: 48,
                mx: mini ? 0.75 : 1,
                my: 0.25,
                px: mini ? 1.25 : 2,
                borderRadius: '8px',
                justifyContent: mini ? 'center' : 'initial',
                color: item.highlight ? '#ffffff' : isActive ? 'primary.main' : 'text.primary',
                bgcolor: item.highlight ? '#050505' : 'transparent',
                border: item.highlight ? '1px solid rgba(0, 0, 0, 0.18)' : '1px solid transparent',
                boxShadow: item.highlight ? '0 6px 18px rgba(0, 0, 0, 0.14)' : 'none',
                '&.Mui-selected': {
                  bgcolor: item.highlight ? '#050505' : 'rgba(0, 51, 102, 0.10)',
                  color: item.highlight ? '#ffffff' : 'primary.main',
                  '&:hover': {
                    bgcolor: item.highlight ? '#171717' : 'rgba(0, 51, 102, 0.15)',
                  },
                },
                '&:hover': {
                  bgcolor: item.highlight ? '#171717' : 'rgba(0, 51, 102, 0.08)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'inherit',
                  justifyContent: 'center',
                  minWidth: 0,
                  mr: mini ? 0 : 2,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ noWrap: true, fontWeight: isActive ? 700 : 600 }}
                sx={{ opacity: mini ? 0 : 1 }}
              />
            </ListItemButton>
          )}
        </NavLink>
      </ListItem>
    </Tooltip>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : open}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? drawerWidth : open ? drawerWidth : `calc(${theme.spacing(8)} + 1px)`,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: isMobile ? drawerWidth : open ? drawerWidth : `calc(${theme.spacing(8)} + 1px)`,
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderRadius: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: mini ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: mini ? 'center' : 'space-between',
          minHeight: mini ? 112 : { xs: 88, sm: 96 },
          gap: mini ? 1.5 : 1,
          px: mini ? 1 : 1.5,
          py: mini ? 1.5 : 0,
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.12)',
          bgcolor: '#050505',
          position: 'relative',
        }}
      >
        {!mini && (
          <Tooltip title="Voltar para produtos" placement="right">
            <IconButton
              onClick={handleBack}
              size="small"
              aria-label="Voltar para produtos"
              sx={{
                ...headerButtonSx,
                width: 44,
                height: 44,
                bgcolor: 'rgba(255, 255, 255, 0.10)',
                border: '1px solid rgba(255, 255, 255, 0.28)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.18)',
                  borderColor: 'rgba(255, 255, 255, 0.42)',
                },
              }}
            >
              <House size={22} strokeWidth={2.2} />
            </IconButton>
          </Tooltip>
        )}

        {!mini && (
          <Box
            component="img"
            src={PRODUCT_IMAGES.cv}
            alt="Curriculo Certo"
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 'auto',
              height: 68,
              maxWidth: 132,
              objectFit: 'contain',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          />
        )}

        <Tooltip title={toggleLabel} placement={mini ? 'right' : 'left'}>
          <IconButton onClick={onToggle} size="small" aria-label={toggleLabel} sx={headerButtonSx}>
            {isMobile ? <X size={22} /> : open ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </IconButton>
        </Tooltip>

        {mini && (
          <Box
            component="img"
            src={PRODUCT_IMAGES.cv}
            alt="Curriculo Certo"
            sx={{
              width: 50,
              height: 34,
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      <List sx={{ py: 1 }}>
        {navItems.map(renderNavItem)}
      </List>

      <Box sx={{ mt: 'auto' }}>
        <List sx={{ py: 0.5 }}>
          {footerNavItems.map(renderNavItem)}
        </List>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: mini ? 'column' : 'row',
            alignItems: 'center',
            gap: mini ? 1 : 1.25,
            px: mini ? 1 : 2,
            py: 1.5,
          }}
        >
          {!mini && (
            <>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'rgba(46, 204, 113, 0.16)',
                  color: 'success.main',
                }}
              >
                <User size={18} />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    textTransform: 'capitalize',
                  }}
                >
                  {userName}
                </Typography>
              </Box>
            </>
          )}
          <Tooltip title="Sair" placement={mini ? 'right' : 'top'}>
            <IconButton
              onClick={handleLogout}
              aria-label="Sair"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                  color: 'error.main',
                },
              }}
            >
              <LogOut size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
